/** @jsxImportSource @emotion/react */
import { darken, lighten } from '@material-ui/core'
import { CSSObject } from '@emotion/styled'
import estyled, { ecss } from '@signal/theme/emotion'
import {
  getColorBackgroundSelector,
  getColorGreySelector,
  getColorPrimarySelector,
  getSpacingSelector
} from '@signal/theme/selectors'
import { MENU_PADDING_SELECTOR, MENU_PADDING_SELECTOR_SMALL } from '../styles'
import { paletteDividerSelector as paletteDividerSelectorRaw } from '@signal/theme/selectors'

const ICON_CONTAINER_SIZE = 50

const paletteDividerSelector = paletteDividerSelectorRaw as unknown as CSSObject

export const MenuItemWrapper = estyled.div`
  display: flex;
  flex-direction: column;
  will-change: transform;
  gap: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px;
`

export const MenuItemContainer = estyled.div<{
  $hasActiveSubItem?: boolean
  $isExpanded?: boolean
  $isDarkMode?: boolean
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ $isExpanded }) =>
    $isExpanded ? 'flex-start' : 'center'};

  transition: all 0.3s;
  cursor: pointer;
  box-sizing: border-box;

  gap: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px;
  padding-left: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px;
  padding-right: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL + 0.5)}px;

  border-radius: 30px;
  border-bottom: 1px solid ${getColorBackgroundSelector('paper')};
  border-top: 1px solid ${getColorBackgroundSelector('paper')};
  border-left: 0;
  border-right: 0;

  z-index: 900;

  ${({ $isExpanded, theme }) =>
    !$isExpanded &&
    ecss`
      gap: 0;
      padding-left: 0;
      padding-right: 0;

      border-radius: 0;
      border-bottom: 1px solid ${paletteDividerSelector};
      border-top: 1px solid ${getColorBackgroundSelector('paper')(theme)};
      border-left: 0;
      border-right: 0;

      justify-content: center;
    `}

  > * {
    &.arrowContainer {
      margin-left: auto;
    }
  }

  svg {
    transition: all 0.1s;
    /* fill: ${getColorGreySelector(600)}; */
    fill: ${getColorGreySelector(600)} !important;
    height: ${ICON_CONTAINER_SIZE / 1.8}px;
    width: ${ICON_CONTAINER_SIZE / 1.8}px;
  }

  path,
  circle {
    transition: all 0.1s;
    fill: ${getColorGreySelector(600)} !important;
  }

  /* Active parent state - when a sub-item is active */
  ${({ $hasActiveSubItem, theme }) =>
    $hasActiveSubItem &&
    `
    
    svg {

      fill: ${getColorPrimarySelector()(theme)} !important;
    }

    path, circle {

      fill: ${getColorPrimarySelector()(theme)} !important;
    }

    .textContainer {
      > * {
        color: ${getColorPrimarySelector()(theme)} !important;
        font-weight: 600 !important;
      }
    }

    div:last-child svg {
      fill: ${getColorPrimarySelector()(theme)} !important;
    }
  `}

  &:hover {
    background-color: ${
      ({ theme }) => getColorBackgroundSelector('lightBlue')(theme) // Normal hover
    };
    transform: scale(1.03);
    z-index: 901;

    svg {
      fill: ${getColorPrimarySelector()} !important;
    }

    path,
    circle {
      fill: ${getColorPrimarySelector()} !important;
    }

    .textContainer {
      > * {
        color: ${getColorPrimarySelector()} !important;
      }
    }

    ${({ $isExpanded, $isDarkMode, theme }) =>
      !$isExpanded &&
      ecss`
        border-radius: 0;
        border-bottom: 1px solid
          ${
            $isDarkMode
              ? darken(getColorPrimarySelector()(theme), 0.3)
              : lighten(getColorPrimarySelector()(theme), 0.6)
          };
        border-top: 1px solid
          ${
            $isDarkMode
              ? darken(getColorPrimarySelector()(theme), 0.3)
              : lighten(getColorPrimarySelector()(theme), 0.6)
          };
        border-left: 0;
        border-right: 0;
        transform: scale(1);
      `}

    /* Show arrow on hover for non-active, closed items and change its color */
    div:last-child {
      opacity: 1 !important;

      svg {
        fill: ${getColorPrimarySelector()} !important;
      }
    }
  }

  &:active {
    transform: scale(0.97);
  }
`

export const IconContainer = estyled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_CONTAINER_SIZE}px;
  height: ${ICON_CONTAINER_SIZE}px;
  border-radius: 5px;
  /* border: 1px solid ${getColorGreySelector(300)};
  box-shadow: inset -3px -3px 2px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.05); */

  /* background-color: ${({ theme }) =>
    lighten(getColorPrimarySelector()(theme), 0.9)}; */
`

export const TextContainer = estyled.div`
  display: flex;
  flex-direction: column;

  font-weight: 600;

  > * {
    font-size: 17px !important;
    color: ${getColorGreySelector(600)} !important;
  }
`

export const SubItemsContainer = estyled.div`
  display: flex;
  flex-direction: column;
  transform-origin: top;
  will-change: transform, opacity;
  gap: ${getSpacingSelector(MENU_PADDING_SELECTOR - 1)}px;
`

export const ArrowContainer = estyled.div<{
  $hasActiveSubItem?: boolean
  $isOpen?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding-right: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px;
  transition:
    opacity 0.2s ease,
    fill 0.2s ease;

  /* Visibility logic based on requirements */
  opacity: ${({ $hasActiveSubItem, $isOpen }) => {
    // Active main option: show always
    if ($hasActiveSubItem) return 0.5
    // Non-active but open: show always
    if ($isOpen) return 0.5
    // Non-active and closed: show only on hover
    return 0
  }};

  svg {
    width: 20px;
    height: 20px;
    fill: ${({ $hasActiveSubItem, theme }) =>
      $hasActiveSubItem
        ? getColorPrimarySelector()(theme)
        : getColorGreySelector(600)(theme)} !important;
    transition:
      fill 0.2s ease,
      transform 0.3s ease;
    transform: rotate(${({ $isOpen }) => ($isOpen ? '-90deg' : '90deg')});
  }
`

// rendered as an anchor (not a div) so the browser treats each entry as a real
// hyperlink: right-click gets the native link context menu, and
// middle/ctrl/cmd-click opens it in a new tab. The resets below keep the
// existing menu look.
export const SubItem = estyled.a<{
  $isActive?: boolean
  $indentLevel?: number
}>`
  color: inherit;
  text-decoration: none;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px
    ${getSpacingSelector(MENU_PADDING_SELECTOR * 2)}px;
  border-radius: 20px;
  cursor: pointer;
  margin-left: ${({ $indentLevel = 1, theme }) =>
    getSpacingSelector(MENU_PADDING_SELECTOR * 2 * $indentLevel)(theme)}px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  ${({ $isActive, theme }) =>
    $isActive
      ? ecss`
          background-color: ${getColorBackgroundSelector('lightBlue')(theme)};
          font-weight: 600;

          > * {
            color: ${getColorPrimarySelector()(theme)} !important;
            font-weight: 600 !important;
          }

          &:hover {
            background-color: ${darken(
              getColorBackgroundSelector('lightBlue')(theme),
              0.1
            )};

            > * {
              color: ${getColorPrimarySelector()(theme)} !important;
            }
          }
        `
      : ecss`
          &:hover {
            background-color: ${getColorBackgroundSelector('lightBlue')(theme)};

            > * {
              color: ${getColorPrimarySelector()(theme)} !important;
            }
          }
        `}

  &:active {
    transform: scale(0.98);
  }
`

export const SubItemArrowContainer = estyled.div<{
  $hasSubItems?: boolean
  $isOpen?: boolean
  $isParentActive?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 12px;
  margin-left: auto;
  margin-right: -8px;
  transition:
    opacity 0.2s ease,
    fill 0.2s ease,
    background-color 0.15s ease;
  cursor: pointer;

  /* Only show if item has sub-items */
  opacity: ${({ $hasSubItems, $isParentActive }) => {
    if (!$hasSubItems) return 0
    // Active parent: show always like main ArrowContainer
    if ($isParentActive) return 0.5
    // Non-active: show always but dimmer
    return 0.7
  }};
  pointer-events: ${({ $hasSubItems }) => ($hasSubItems ? 'auto' : 'none')};

  &:hover {
    background-color: ${getColorBackgroundSelector('lightBlue')};
    opacity: 1;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: ${({ $isParentActive, theme }) =>
      $isParentActive
        ? getColorPrimarySelector()(theme)
        : getColorGreySelector(600)(theme)} !important;
    transition:
      fill 0.2s ease,
      transform 0.3s ease;
    transform: rotate(${({ $isOpen }) => ($isOpen ? '-90deg' : '90deg')});
  }

  &:hover svg {
    fill: ${getColorPrimarySelector()} !important;
  }
`
